/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  APP_INITIALIZER,
  LOCALE_ID,
  ModuleWithProviders,
  NgModule,
  inject,
} from '@angular/core';
import {findLanguage, LocaleService, setupInitialLocale} from './locale.service';
import {NZ_DATE_CONFIG, NZ_DATE_LOCALE, NZ_I18N} from 'ng-zorro-antd/i18n';
import {DateFnsConfigurationService} from 'ngx-date-fns';

/**
 * This module is used to initialize all the DI tokens needed for the locale
 * Those include:
 * - Setting the LOCALE_ID
 * - Setting the NZ_I18N used by NgZorro
 * - Initializing the default locale on the APP_INITIALIZER
 */
@NgModule()
export class PspLocaleModule {
  constructor() {
    const parentModule = inject(PspLocaleModule, {optional: true, skipSelf: true});

    if (parentModule) {
      throw new Error('PspLocaleModule is already loaded. Import it in the AppModule only');
    }
  }

  static forRoot(): ModuleWithProviders<PspLocaleModule> {
    return {
      ngModule: PspLocaleModule,
      providers: [
        {
          provide: APP_INITIALIZER,
          useFactory: setupInitialLocale,
          deps: [LocaleService],
          multi: true,
        },
        {
          provide: LOCALE_ID,
          useFactory: (localeService: LocaleService) => localeService.getLocale(),
          deps: [LocaleService],
        },
        {
          provide: NZ_I18N,
          useFactory: (localeService: LocaleService) => {
            const locale = localeService.getLocale();
            const language = findLanguage(locale);
            return language?.ngZorroLocale;
          },
          deps: [LocaleService],
        },
        {
          provide: NZ_DATE_LOCALE,
          useFactory: (localeService: LocaleService) => {
            const locale = localeService.getLocale();
            const language = findLanguage(locale);
            return language?.dateFnsLocale;
          },
          deps: [LocaleService],
        },
        {provide: NZ_DATE_CONFIG, useValue: {firstDayOfWeek: 6}}, // Sunday will be 0
        {
          /**
           * Provider used by ngx-date-fns library to globally change the default locale
           */
          provide: DateFnsConfigurationService,
          useFactory: (localeService: LocaleService) => {
            const locale = localeService.getLocale();
            const language = findLanguage(locale);

            const localizedDateFnsConfigurationService = new DateFnsConfigurationService();
            localizedDateFnsConfigurationService.setLocale(language?.dateFnsLocale);

            return localizedDateFnsConfigurationService;
          },
          deps: [LocaleService],
        },
      ],
    };
  }
}
